import { DagreLayout } from '@antv/layout';
import { Graph } from '@antv/x6';
import { Keyboard } from '@antv/x6-plugin-keyboard';
import { Scroller } from '@antv/x6-plugin-scroller';
import { Selection } from '@antv/x6-plugin-selection';
import { v4 as uuid } from 'uuid';

import { cloneDeep } from 'lodash';
import { NodeType } from '@amplify/amp-module';

import defaultGraphConfig from './default-graph-config';
import { StopPreview } from '@/constants/actions';
import { defaultNodesData, START_NODE } from '@/constants/builder';
import {
  GetConversationNodesById,
  GetConversationEdgesById,
  GetNodeById,
  GetSelectedNodeId,
  GetSelectedSubNode,
} from '@/constants/getters';
import {
  SetCurrentNode,
  SetHighlightedNode,
  SetSelectedNodeId,
  SetSelectedSubNode,
  SetZoom,
} from '@/constants/mutations';
import { getNodeActualSize, hoverNode, isInsideCoords, toggleNodeSelection } from '@/utils/builder-utils';
import { sortBySortIndex } from '@/utils/amp-utils';

export default class BuilderGraph {
  #context;
  #store;
  #dagreLayout;
  #graph;
  #conversationId;
  #renderedNodes;
  #isReadonly;

  constructor(ctx, containerElement, { conversationId, graphOptions, reviewPage = false, readonly = false } = {}) {
    this.graphId = uuid();
    this.#context = ctx;
    this.#conversationId = conversationId;
    this.#store = ctx.store;

    this.#graph = new Graph({ ...defaultGraphConfig, ...(graphOptions || {}), container: containerElement });
    this.#graph.amplifyGraphId = this.graphId;

    this.#isReadonly = readonly;
    this.#renderedNodes = [];

    if (reviewPage) {
      this.#graph.container.addEventListener('amplify:node:render:done', this.renderListener);
    }
  }

  renderListener = (e) => {
    const conversationNodes = this.#store.getters[`builderStore/${GetConversationNodesById}`](this.#conversationId);
    const { id: nodeId } = e.detail;

    // Graph has no nodes, emit ready event immediately
    if (nodeId === START_NODE.id) {
      this.#emitGraphReadyEvent();
    } // Has the node somehow fired the event twice?
    else if (!this.#renderedNodes.find((node) => node.id === nodeId)) {
      this.#renderedNodes.push(e.detail);

      // If there is now a total number of rendered nodes corresponding to the expected number of nodes in the conversation
      if (this.#renderedNodes.length === conversationNodes.length) {
        this.adjustLayout();
        this.#emitGraphReadyEvent();
      }
    } else {
      console.error(`This rendered node already exists in the renderedNodesArray for ${this.conversationId}!`, nodeId);
    }
  };

  get graph() {
    return this.#graph;
  }

  get conversationId() {
    return this.#conversationId;
  }

  #emitGraphReadyEvent() {
    document.dispatchEvent(
      new CustomEvent('amplify:graph:render:ready', { bubbles: true, detail: { id: this.#conversationId } })
    );

    this.#graph.container.removeEventListener('amplify:node:render:done', this.renderListener);
  }

  #getNodesDataForLayout() {
    const nodes = this.#graph.getNodes();

    return nodes.map((node) => {
      const nodeData = this.#store.getters[`builderStore/${GetNodeById}`]({
        id: node.id,
        conversationId: this.#conversationId,
      });
      const renderedNode = this.#renderedNodes.find((n) => n.id === node.id);

      const nodeSize = getNodeActualSize(node, nodeData);
      const { height } = renderedNode?.size ? renderedNode.size : nodeSize;
      const { width } = nodeSize;

      const { height: oldHeight, width: oldWidth } = node.size();
      const { x, y } = node.getPosition();
      const { selected } = node.getData();

      if (width !== oldWidth || height !== oldHeight) {
        node.resize(width, height);
      }

      if (selected && !renderedNode) {
        toggleNodeSelection(node, nodeData, true);
      }

      return {
        id: node.id,
        x: x - width / 2,
        y: y - height / 2,
        shape: node.shape,
        width,
        height,
      };
    });
  }

  /**
   * Center node or sub node of graph
   * @param {String|Node} node - the node to center on
   * @param {String|Number} options.animationDuration - the duration of the animation
   * @param {Number} options.subNodeIndex - the index of selected sub node of the node to center on
   * @param {Object} options.coordinates - coordinates (x, y) of the cursor - used to identify the sub node
   * @param {Boolean} options.isInital - Wether or not it is the initial center call
   */
  centerNode(node, options = {}) {
    const cell = typeof node === 'string' ? this.#graph.getCellById(node) : node;

    if (!cell) {
      return;
    }

    const { animationDuration = 200, coordinates = {}, isInital = false } = options;
    let { subNodeIndex = -1 } = options;

    const centerOptions = {
      animation: {
        duration: animationDuration || 200,
        easing: 'linear',
      },
    };

    const nodeType = cell.id.split('-')[0];
    const isGroupNode = [NodeType.buttonGroup, NodeType.carousel, NodeType.question].includes(nodeType);

    if (isGroupNode) {
      const position = cell.position();
      const { width, height } = cell.size();
      const { x = 0, y = 0 } = coordinates;

      // get the question node message height
      const elem = nodeType === NodeType.question && document.getElementById(cell.id + '-message');
      const mainNodeHeight = elem ? elem.offsetHeight + 32 : 0; // node height + 32px margin top & bottom

      const subNodeWidth = 280; // 264px node width + 16px margin
      const subNodeHeight = nodeType === NodeType.question ? height - mainNodeHeight : height;
      const actionButtonsHeight = 56; // the height of the node action buttons
      const nbOfSubNodes = parseInt((width - 16) / subNodeWidth); // decrease the margin of the group node
      const subNodes = [...Array(nbOfSubNodes).keys()]; // array with sub nodes indexes

      const getSubNodeCoordinates = (index) => {
        // top left corner
        const x1 = position.x + subNodeWidth * index;
        const y1 = position.y + mainNodeHeight;
        // bottom right corner
        const x2 = x1 + subNodeWidth;
        const y2 = y1 + subNodeHeight + actionButtonsHeight;

        return { x1, y1, x2, y2 };
      };

      let subNodePosition = null;

      if (subNodeIndex >= 0) {
        // use the coordinates of given sub node
        const { x1, y1 } = getSubNodeCoordinates(subNodeIndex);

        subNodePosition = { x: x1, y: y1 };
      } else if (!isInital) {
        // find the coordinates of sub node based on the given coordinates (x, y)
        subNodes.every((index) => {
          const { x1, y1, x2, y2 } = getSubNodeCoordinates(index);

          if (isInsideCoords(x1, y1, x2, y2, x, y)) {
            subNodePosition = { x: x1, y: y1 };
            subNodeIndex = index;
            return false;
          }

          return true;
        });
      }

      if (subNodePosition) {
        this.#store.commit(`builderStore/${SetSelectedSubNode}`, subNodeIndex);
        this.#graph.positionRect(
          { ...subNodePosition, width: subNodeWidth, height: subNodeHeight },
          'center',
          centerOptions
        );

        return;
      }
    }

    // center entire node if node is not a group node or sub node is not found
    this.#graph.centerCell(cell, centerOptions);
  }

  init(options) {
    this.#registerPlugins(options?.pluginOptions || {});
    this.#registerKeybindings();
    this.#registerListeners();

    this.#dagreLayout = new DagreLayout({
      type: 'dagre',
      begin: [280, 80],
      rankdir: 'TB',
      nodeOrder: [],
      controlPoints: true,
      nodesepFunc: (node) => {
        const { shape, width } = node;
        const wideNodeShapes = ['buttonGroup-node', 'carousel-node', 'question-node'];

        if (wideNodeShapes.includes(shape)) {
          return width / 2 - 15;
        }

        return 135;
      },
      ranksepFunc: (node) => {
        const { id, shape } = node;
        const highNodeShapes = ['carousel-node', 'question-node', 'player-node'];
        const renderedNode = this.#renderedNodes.find((n) => n.id === id);

        if (renderedNode?.size?.height) {
          return renderedNode.size.height / 2;
        }

        if (highNodeShapes.includes(shape)) {
          return 200;
        }

        if (shape === 'condition-node' || shape === 'buttonGroup-node') {
          return 130;
        }

        return 80;
      },
    });

    return this.graph;
  }

  /**
   * @param {String|Number} options.conversationId - the ID of the rendered conversation
   * @param {Boolean} options.initialTransform - if it's the initial transform - animations will be disabled
   * @param {String|Number} options.nodeId - the nodeId that the graph should center on
   * @param {Number} options.subNodeIndex - the index of selected sub node of nodeId
   * @param {Boolean} options.setSelectedNode - render the current selected node
   * @param {Boolean} options.shouldCenterOnNode - center on a node (either nodeId or the selected node) or not
   * @param {Boolean} options.shouldSetCenterPosition - set a center or not (can be used to rerender without changing center position)
   */
  transform(options = {}) {
    const {
      conversationId,
      initialTransform = false,
      setSelectedNode = true,
      shouldCenterOnNode = true,
      shouldSetCenterPosition = true, // wether or not the graph should set a center position on render or not
    } = options;
    let { nodeId = null, subNodeIndex = -1 } = options;

    if (conversationId) {
      this.#conversationId = conversationId;
    }

    if (!this.#graph) {
      return;
    }

    const selectedNodeId = setSelectedNode ? this.#store.getters[`builderStore/${GetSelectedNodeId}`] : null;
    const nodeArr = this.#store.getters[`builderStore/${GetConversationNodesById}`](this.#conversationId)
      // remove undefined entries to avoid node rendering issues
      .filter((node) => node);

    if (!nodeId && shouldCenterOnNode && shouldSetCenterPosition) {
      // center the selected node if the node exists or the first node in the conversation
      if (selectedNodeId && nodeArr.some((node) => node.id === selectedNodeId)) {
        nodeId = selectedNodeId;
        subNodeIndex = this.#store.getters[`builderStore/${GetSelectedSubNode}`];
      } else {
        nodeId = (nodeArr.find((node) => !node.parentId) ?? nodeArr[0])?.id;
        subNodeIndex = -1;
      }
    }

    const nodes = nodeArr.map((node) => {
      const { id, sortIndex, parentId } = node;
      const res = { id, sortIndex, parentId, data: {} };

      if (this.#conversationId) {
        res.data.conversationId = this.#conversationId;
      }

      const mergedData = { ...defaultNodesData.UI.node, ...defaultNodesData.UI[node.type] };

      for (const key of Object.keys(mergedData))
        res[key] =
          typeof mergedData[key] === 'object'
            ? { ...cloneDeep(mergedData[key]), ...(res[key] || {}) }
            : mergedData[key];

      // set conversation start node
      if (!res.parentId) {
        res.data.conversationStarter = true;
      }

      // select node
      if (setSelectedNode && id === nodeId) {
        res.data.selected = true;
      }

      if (res?.id?.includes('condition')) {
        res.data.children = this.#checkForConditionBranch(res.id, nodeArr);
      }

      return res;
    });

    if (!nodes.length) {
      nodes.push(START_NODE);
    }

    this.render(nodes, {
      nodeId,
      initialTransform,
      subNodeIndex,
      centerContent: shouldSetCenterPosition,
      selectNode: setSelectedNode,
    });
  }

  adjustLayout() {
    const edges = this.#graph.getEdges();
    const nodes = this.#graph.getNodes();

    const dagreLayoutInput = {
      nodes: this.#getNodesDataForLayout(),
      edges: edges.map((e) => ({ id: e.id, source: e.getSourceCellId(), target: e.getTargetCellId() })),
    };

    if (nodes.length > 1) {
      this.#dagreLayout.nodeOrder = nodes.sort(sortBySortIndex).map((n) => n.id);
    }

    const dagreLayoutData = this.#dagreLayout.layout(dagreLayoutInput);

    for (const node of nodes) {
      const newNode = dagreLayoutData.nodes.find((n) => n.id === node.id);

      if (newNode) {
        const nodeData = {};
        const allowedKeys = Object.keys(node).filter((k) => !['shape', 'id', 'x', 'y', 'width', 'height'].includes(k));

        for (const key of allowedKeys) {
          nodeData[key] = node[key];
        }

        /**
         * It should be noted that the x and y returned by the layout algorithm are actually the coordinates of the
         * center point of the node. In X6, the x and y of the node are actually the coordinates of the upper left
         * corner, so after the layout, we need to do a coordinate transformation.
         */

        const { width, height } = node.size();

        node
          .setPosition({
            x: newNode.x - width / 2,
            y: newNode.y - height / 2, // remove / add  '- height / 2' if you want to align nodes on top / centered
          })
          .setData({ ...newNode, ...nodeData });
      }
    }

    const edgePoints = {};
    for (const edge of edges) {
      const newEdge = dagreLayoutData.edges.find((e) => e.id === edge.id);

      // Getting cached edge position or BBox data for performance
      const getEdgeData = (edg, type = 'target') => {
        let method = '';
        switch (type) {
          case 'sourceBox':
            method = 'getSourceNode';
            break;
          case 'targetBox':
            method = 'getTargetNode';
            break;
          case 'source':
            method = 'getSourcePoint';
            break;
          case 'target':
          default:
            method = 'getTargetPoint';
            break;
        }

        let point = edgePoints[edg.id]?.[method];

        if (!point) {
          point = type.includes('Box') ? edg[method]().getBBox() : edg[method]();
          edgePoints[edg.id] = edgePoints[edg.id] || {};
          edgePoints[edg.id][method] = point;
        }

        return point;
      };

      if (newEdge) {
        const sourceBBox = getEdgeData(edge, 'sourceBox');
        const targetBBox = getEdgeData(edge, 'targetBox');
        const diff = targetBBox.top - sourceBBox.bottom;

        // the vertex position
        const x = targetBBox.center.x;
        const y = Math.round((sourceBBox.bottom + diff / 2) / 10) * 10;

        // check if the vertex is overlapped by an outside node edge
        const isInnerLeft = (edg) => x > getEdgeData(edg, 'target').x && x < getEdgeData(edg, 'source').x;
        const isInnerRight = (edg) => x < getEdgeData(edg, 'target').x && x > getEdgeData(edg, 'source').x;
        const isInnerNode = (edg) => isInnerLeft(edg) || isInnerRight(edg);

        // check if the vertex is overlapped by an inside node edge
        const isOuterLeft = (edg) =>
          getEdgeData(edg, 'target').x > x && getEdgeData(edg, 'target').x < getEdgeData(edge, 'source').x;
        const isOuterRight = (edg) =>
          getEdgeData(edg, 'target').x < x && getEdgeData(edg, 'target').x > getEdgeData(edge, 'source').x;
        const isOuterNode = (edg) => isOuterLeft(edg) || isOuterRight(edg);

        const isSameRow = (edg) => getEdgeData(edg, 'sourceBox').center.y === sourceBBox.center.y;
        const isLeftNode = (edg) => isSameRow(edg) && (isInnerLeft(edg) || isOuterLeft(edg));
        const isRightNode = (edg) => isSameRow(edg) && (isInnerRight(edg) || isOuterRight(edg));

        // calculate the value by which the vertex position must be changed
        const adjustedPosition = (value = 0) => {
          const adjustedY = y + value;

          // If the adjusted Y position get's too close to the target or source node, then we'll stop here
          if (adjustedY + 30 >= targetBBox.top || adjustedY - 30 <= sourceBBox.bottom) return value;

          // Find any overlapping edges
          const overlappedEdge = edges
            // filter the edges with vertices on the same line with the new vertex
            // or on the same row where the vertices overlap (the edges are always rendered left to right)
            .filter(
              (edg) =>
                edg.vertices?.[0]?.y === adjustedY ||
                (isLeftNode(edg) && edg.vertices?.[0]?.y > adjustedY) || // if the edg is to the left of the edge and the vertice is
                (isRightNode(edg) && edg.vertices?.[0]?.y < adjustedY)
            )
            // find the edge that will be overlapped by the current edge
            .find((edg) => isInnerNode(edg) || isOuterNode(edg));

          if (overlappedEdge) {
            if (isInnerNode(overlappedEdge)) {
              value += 10;
            } else if (isOuterNode(overlappedEdge)) {
              value -= 10;
            }

            return adjustedPosition(value);
          }

          return value;
        };

        edge.vertices = [{ x, y: y + adjustedPosition() }];
      }
    }
  }

  /**
   * @param {Array} nodesArr - the nodes of the graph
   * @param {Boolean} options.initialTransform - if it's the initial transform - animations will be disabled
   * @param {String|Number} options.nodeId - the nodeId that the graph should center on
   * @param {Number} options.subNodeIndex - the index of selected sub node of nodeId
   * @param {Boolean} options.centerContent - whether or not to center on node (or entire content if nodeId is null)
   * @param {Boolean} options.selectNode - whether or not to select node
   */
  render(nodesArr = [], options = {}) {
    if (!this.#graph) {
      return;
    }

    const {
      nodeId = null,
      subNodeIndex = -1,
      centerContent = true,
      selectNode = true,
      initialTransform = false,
    } = options;

    // clear graph data and insert new data
    this.#graph.clearCells({ silent: true });

    const parsedEdges = this.#store.getters[`builderStore/${GetConversationEdgesById}`](this.#conversationId);

    this.#graph.fromJSON({
      nodes: nodesArr,
      edges: parsedEdges,
    });

    // select node
    if (selectNode && nodeId && nodeId !== 'start-node') {
      this.#graph.resetSelection(this.#graph.getCellById(nodeId));
    }

    this.adjustLayout();

    // center node or entire graph content
    if (centerContent) {
      nodeId
        ? this.centerNode(nodeId, { subNodeIndex, animationDuration: initialTransform ? 0 : undefined, isInital: initialTransform })
        : this.#graph.centerContent({ animation: { duration: initialTransform ? 0 : 200, easing: 'linear' } });
    }
  }

  #registerPlugins(options) {
    this.#graph.use(
      new Selection({
        enabled: true,
        multiple: false,
        ...(options?.selection || {}),
      })
    );

    this.#graph.use(
      new Scroller({
        enabled: true,
        pannable: true,
        autoResize: true,
        autoResizeOptions: {
          border: 400,
        },
        ...(options?.scroller || {}),
      })
    );

    this.#graph.use(
      new Keyboard({
        enabled: true,
        guard(e) {
          if (e.code === 'Space') {
            // prevent spacebar scroll action
            if (e.target.classList.contains('x6-graph-scroller')) {
              e.preventDefault();
            }

            return false;
          }

          return true;
        },

        ...(options?.keyboard || {}),
      })
    );
  }

  #registerKeybindings() {
    this.#graph.bindKey(['ctrl+1', 'meta+1'], () => {
      const zoom = this.#graph.zoom();

      if (zoom < 1.5) {
        this.#graph.zoom(0.1);
      }
    });

    this.#graph.bindKey(['ctrl+2', 'meta+2'], () => {
      const zoom = this.#graph.zoom();

      if (zoom > 0.5) {
        this.#graph.zoom(-0.1);
      }
    });

    this.#graph.bindKey('Space', () => {});
  }

  #registerListeners() {
    this.#graph.on('scale', ({ sx }) => {
      this.#store.commit(`builderStore/${SetZoom}`, parseInt(sx * 100));
    });

    this.#graph.on('node:mousedown', () => {
      this.#graph.disablePanning();
    });

    this.#graph.on('node:mouseup', () => {
      this.#graph.enablePanning();
    });

    this.#graph.on('node:mouseenter', ({ node }) => {
      hoverNode(node, true);
    });

    this.#graph.on('node:mouseleave', ({ node }) => {
      hoverNode(node, false);
    });

    this.#graph.on('node:selected', ({ node }) => {
      if (this.#isReadonly) {
        return;
      }

      const data = this.#store.getters[`builderStore/${GetNodeById}`]({
        id: node.id,
        conversationId: this.#conversationId,
      });

      toggleNodeSelection(node, data, true);

      this.#store.commit(`builderStore/${SetSelectedNodeId}`, node.id);
      this.#store.commit(`builderStore/${SetSelectedSubNode}`, -1);
      this.#store.commit(`previewStore/${SetCurrentNode}`, node.id);
    });

    this.#graph.on('node:unselected', ({ node }) => {
      if (this.#isReadonly) {
        return;
      }

      const data = this.#store.getters[`builderStore/${GetNodeById}`]({
        id: node.id,
        conversationId: this.#conversationId,
      });
      const selectedNodeId = this.#store.getters[`builderStore/${GetSelectedNodeId}`];

      toggleNodeSelection(node, data, false);

      if (node.id === selectedNodeId) {
        this.#store.commit(`builderStore/${SetSelectedNodeId}`, '');
        this.#store.commit(`builderStore/${SetSelectedSubNode}`, -1);
      }
    });

    this.#graph.on('node:click', ({ node, x, y }) => {
      // Since the user was hovering the node when clicking and we will move the graph around
      // it will leave the clicked node in a hovered state.
      hoverNode(node, false);
      this.centerNode(node, { coordinates: { x, y } });
      this.#store.dispatch(`previewStore/${StopPreview}`);
    });

    this.#graph.on('blank:click', () => {
      this.#store.commit(`builderStore/${SetHighlightedNode}`, null);
      this.#store.commit(`previewStore/${SetCurrentNode}`, '');
      this.#store.dispatch(`previewStore/${StopPreview}`);
    });

    /**
     * Functions for setting up UI specific to a given node,
     * that cannot be inferred from simply the component, but is instead based on the data state
     * @example setupButtonGroup(UINode, dataNode) {}
     */
  }

  #checkForConditionBranch(id, nodes) {
    const branching = {
      trueBranch: false,
      falseBranch: false,
    };

    nodes.forEach((n) => {
      if (n.parentId?.includes(`${id}_port_true`)) {
        branching.trueBranch = true;
      }

      if (n.parentId?.includes(`${id}_port_false`)) {
        branching.falseBranch = true;
      }
    });

    return branching;
  }
}
